/*

  Actions.ts

  Single page UI for Netskrafl/Explo using the Mithril library

  Copyright (C) 2025 Miðeind ehf.
  Author: Vilhjálmur Þorsteinsson

  The Creative Commons Attribution-NonCommercial 4.0
  International Public License (CC-BY-NC 4.0) applies to this software.
  For further information, see https://github.com/mideind/Netskrafl

  This file implements the Actions class.

*/

import { ServerGame, ChallengeParameters, UserListCriteria, IView, RiddleWord, BestScore, ChatMessage } from "./types";
import { Params, scrollMovelistToBottom, playAudio } from "./util";
import { m } from "./mithril";
import {
  attachFirebaseListener, detachFirebaseListener, logEvent
} from "./channel";
import { Model } from "./model";
import { request } from "./request";
import { saveAuthSettings } from "./authSettings";

export class Actions {

  model: Model;

  constructor(model: Model) {
    this.model = model;
    // Media and Firebase listeners will be initialized
    // when view is available
  }

  onNavigateTo(routeName: string, params: Params, view: IView) {
    // We have navigated to a new route
    // If navigating to something other than help,
    // we need to have a logged-in user
    const model = this.model;
    model.routeName = routeName;
    model.params = params;
    const uuid = params.uuid ?? "";
    if (routeName == "game") {
      // New game route: initiate loading of the game into the model
      if (model.game !== null) {
        this.detachListenerFromGame(model.game.uuid);
      }
      // If opening this game as a zombie, remove zombie status
      const deleteZombie = params.zombie === "1";
      // Load the game, and attach it to the Firebase listener once it's loaded
      model.loadGame(
        uuid,
        () => {
          this.attachListenerToGame(uuid, view);
          setTimeout(scrollMovelistToBottom);
          if (!model.state?.uiFullscreen)
            // Mobile UI: show board tab
            view.setSelectedTab("board");
        },
        deleteZombie
      );
      if (model.game !== null && model.game !== undefined) {
        logEvent("game_open",
          {
            locale: model.game.locale,
            uuid: params.uuid,
          }
        );
      }
    } else if (routeName == "review") {
      // A game review: detach listener, if any, and load
      // new game if necessary
      if (model.game !== null) {
        // !!! This may cause an extra detach - we assume that's OK
        this.detachListenerFromGame(model.game.uuid);
      }
      // Find out which move we should show in the review
      let moveParam: string = params.move || "0";
      // Start with move number 0 by default
      let move = parseInt(moveParam);
      if (isNaN(move) || !move || move < 0)
        move = 0;
      if (model.game === null || model.game.uuid != params.uuid) {
        // Different game than we had before: load it, and then
        // fetch the best moves
        model.loadGame(uuid, () => {
          model.loadBestMoves(move);
          setTimeout(scrollMovelistToBottom);
          if (!model.state?.uiFullscreen)
            // Mobile UI: show board tab
            view.setSelectedTab("board");
        });
      } else if (model.game !== null) {
        // Already have the right game loaded:
        // Fetch the best moves and show them once they're available
        model.loadBestMoves(move);
      }
    } else {
      // Not a game route: delete the previously loaded game, if any
      if (model.game !== null) {
        this.detachListenerFromGame(model.game.uuid);
        model.game.cleanup();
        model.game = null;
      }
      const locale = model.state?.locale || "is_IS";
      if (routeName == "help") {
        // Make sure that the help HTML is loaded upon first use
        model.loadHelp();
        logEvent("help", { locale });
      } else if (routeName == "main") {
        // Force reload of lists
        // TODO: This may not be necessary,
        // if all Firebase notifications are acted upon
        view.actions.withSpinner(() => model.loadAllLists());
        model.userListCriteria = null;
        model.userList = null;
      }
    }
  }

  onMoveMessage(json: ServerGame, firstAttach: boolean) {
    // Handle a move message from Firebase
    // console.log("Move message received: " + JSON.stringify(json));
    this.model.handleMoveMessage(json, firstAttach);
  }

  onUserChallengeMessage(json: any, firstAttach: boolean) {
    // Handle a user challenge message from Firebase
    // console.log("User challenge message received: " + JSON.stringify(json));
    this.model.handleUserChallengeMessage(json, firstAttach);
  }

  onUserMoveMessage(json: any, firstAttach: boolean) {
    // Handle a user move message from Firebase
    // console.log("User move message received: " + JSON.stringify(json));
    this.model.handleUserMoveMessage(json, firstAttach);
  }

  onChatMessage(
    json: ChatMessage,
    firstAttach: boolean,
    view: IView,
  ) {
    // Handle an incoming chat message
    if (firstAttach) {
      // console.log("First attach of chat: " + JSON.stringify(json));
    } else {
      // console.log("Chat message received: " + JSON.stringify(json));
      if (this.model.addChatMessage(json.game, json.from_userid, json.msg, json.ts)) {
        // A chat message was successfully added
        view.notifyChatMessage();
        // Play audio notification if:
        // - User has audio enabled
        // - Message is from opponent (not from current user)
        const { state } = this.model;
        const userId = state.netskraflUserId ?? "";
        if (state.audio && json.from_userid !== userId) {
          playAudio(state, "new-msg");
        }
      }
    }
  }

  onFullScreen(view?: IView) {
    // Take action when min-width exceeds 768
    const state = this.model.state;
    if (state && !state.uiFullscreen) {
      state.uiFullscreen = true;
      if (view) {
        // Reset zoom when switching to fullscreen (desktop always uses scale 1)
        view.resetScale();
        view.notifyMediaChange();
      }
      m.redraw();
    }
  }

  onMobileScreen(view?: IView) {
    const state = this.model.state;
    if (state && state.uiFullscreen !== false) {
      state.uiFullscreen = false;
      if (view) {
        view.notifyMediaChange();
      }
      m.redraw();
    }
  }

  onLandscapeScreen(view?: IView) {
    const state = this.model.state;
    if (state && !state.uiLandscape) {
      state.uiLandscape = true;
      if (view) {
        view.notifyMediaChange();
      }
      m.redraw();
    }
  }

  onPortraitScreen(view?: IView) {
    const state = this.model.state;
    if (state && state.uiLandscape !== false) {
      state.uiLandscape = false;
      if (view) {
        view.notifyMediaChange();
      }
      m.redraw();
    }
  }

  mediaMinWidth667(mql: MediaQueryList, view?: IView) {
    if (mql.matches) {
      // Take action when min-width exceeds 667
      // (usually because of rotation from portrait to landscape)
      // The board tab is not visible, so the movelist is default
      this.onLandscapeScreen(view);
    }
    else {
      // min-width is below 667
      // (usually because of rotation from landscape to portrait)
      // Make sure the board tab is selected
      this.onPortraitScreen(view);
    }
  }

  mediaMinWidth768(mql: MediaQueryList, view?: IView) {
    if (mql.matches) {
      this.onFullScreen(view);
    }
    else {
      this.onMobileScreen(view);
    }
  }

  initMediaListener(view: IView) {
    // Install listener functions for media changes

    function addEventListener(mql: MediaQueryList, func: (ev: MediaQueryListEvent) => void) {
      // Hack to make addEventListener work on older Safari platforms
      try {
        // Chrome & Firefox
        mql.addEventListener('change', func, { passive: true });
      } catch (e1) {
        try {
          // Safari
          mql.addListener(func);
        } catch (e2) {
          console.error(e2);
        }
      }
    }

    let mql: MediaQueryList = window.matchMedia("(min-width: 667px)");
    const actions = this;
    if (mql) {
      this.mediaMinWidth667(mql, view);
      addEventListener(mql, () => actions.mediaMinWidth667(mql, view));
    }
    mql = window.matchMedia("(min-width: 768px)");
    if (mql) {
      this.mediaMinWidth768(mql, view);
      addEventListener(mql, () => actions.mediaMinWidth768(mql, view));
    }
  }

  attachListenerToUser() {
    const state = this.model.state;
    // console.log(`attachListenerToUser(): netskraflUserId=${state?.netskraflUserId}`);
    if (!state || !state.netskraflUserId) return;
    // Listen to challenge and move notifications separately
    attachFirebaseListener(`user/${state.netskraflUserId}/challenge`,
      (json, firstAttach) => this.onUserChallengeMessage(json, firstAttach)
    );
    attachFirebaseListener(`user/${state.netskraflUserId}/move`,
      (json, firstAttach) => this.onUserMoveMessage(json, firstAttach)
    );
  }

  detachListenerFromUser() {
    // Stop listening to Firebase notifications for the current user
    const state = this.model.state;
    // console.log(`detachListenerFromUser(): netskraflUserId=${state?.netskraflUserId}`);
    if (state && state.netskraflUserId)
      detachFirebaseListener('user/' + state.netskraflUserId);
  }

  attachListenerToGame(uuid: string, view: IView) {
    // Listen to Firebase events on the /game/[gameId]/[userId] path
    const state = this.model.state;
    if (!uuid || !state) return;
    const basepath = `game/${uuid}/${state.netskraflUserId}/`;
    // New moves
    attachFirebaseListener<ServerGame>(basepath + "move",
      (json, firstAttach) => this.onMoveMessage(json, firstAttach)
    );
    // New chat messages
    attachFirebaseListener<ChatMessage>(basepath + "chat",
      (json, firstAttach) => this.onChatMessage(json, firstAttach, view)
    );
  }

  detachListenerFromGame(uuid: string) {
    // Stop listening to Firebase events on the /game/[gameId]/[userId] path
    const state = this.model.state;
    if (!uuid || !state) return;
    const basepath = `game/${uuid}/${state.netskraflUserId}/`;
    detachFirebaseListener(basepath + "move");
    detachFirebaseListener(basepath + "chat");
  }

  async markFavorite(userId: string, status: boolean) {
    // Mark or de-mark a user as a favorite
    if (!this.model.state) return;
    try {
      await request(this.model.state, {
        method: "POST",
        url: "/favorite",
        body: { destuser: userId, action: status ? "add" : "delete" }
      });
    } catch(e) {
      // No need to do anything here - a future TODO is to indicate an error in the UI
    }
  }

  async toggleFavorite(userId: string, currentStatus: boolean) {
    // Toggle favorite status for a user
    await this.markFavorite(userId, !currentStatus);
  }

  // Challenge Management Actions
  async handleChallenge(parameters: ChallengeParameters) {
    // Reject or retract a challenge
    if (!this.model.state) return;
    try {
      const json: { result: number; } = await request(this.model.state, {
        method: "POST",
        url: "/challenge",
        body: parameters
      });
      if (json?.result === 0) {
        // Log the change of challenge status (issue/decline/retract/accept)
        const locale = this.model.state?.locale || "is_IS";
        var p: any = { locale };
        if (parameters.duration !== undefined)
          p.duration = parameters.duration;
        if (parameters.fairplay !== undefined)
          p.fairplay = parameters.fairplay;
        if (parameters.manual !== undefined)
          p.manual = parameters.manual;
        logEvent("challenge_" + parameters.action, p);
        // Reload list of challenges from server
        this.model.loadChallengeList();
        if (this.model.userListCriteria)
          // We are showing a user list: reload it
          this.model.loadUserList(this.model.userListCriteria);
      }
    } catch(e) {
      // A future TODO is to indicate an error in the UI
    }
  }

  async acceptChallenge(destuser: string, key: string) {
    // Accept a challenge
    await this.handleChallenge({
      action: "accept",
      destuser,
      key
    });
  }

  async declineChallenge(destuser: string, key: string) {
    // Decline a challenge
    await this.handleChallenge({
      action: "decline",
      destuser,
      key
    });
  }

  async retractChallenge(destuser: string, key: string) {
    // Retract a challenge
    await this.handleChallenge({
      action: "retract",
      destuser,
      key
    });
  }

  // Game Management Actions
  async startNewGame(oppid: string, reverse: boolean = false) {
    // Ask the server to initiate a new game against the given opponent
    if (!this.model.state) return;
    try {
      const rqBody: {
        opp: string;
        rev: boolean;
        board_type?: string
      } = { opp: oppid, rev: reverse };
      if (this.model.isExplo) {
        // On an Explo client, always use the Explo board,
        // regardless of the user's locale setting
        rqBody.board_type = "explo";
      }
      const rq = {
        method: "POST",
        url: "/initgame",
        body: rqBody
      };
      const json = await request<{ ok: boolean; uuid: string; }>(this.model.state, rq);
      if (json?.ok) {
        // Log the new game event
        const locale = this.model.state?.locale || "is_IS";
        logEvent("new_game",
          {
            uuid: json.uuid,
            timed: reverse,
            locale
          }
        );
        // Go to the newly created game
        m.route.set("/game/" + json.uuid);
      }
    } catch(e) {
      // No need to do anything
    }
  }

  async startRobotGame(oppid: string) {
    // Start a new game against a robot opponent
    await this.startNewGame(oppid, false);
  }

  // User Management Actions
  async loadUsers(criteria: UserListCriteria, activateSpinner: boolean = true) {
    // Load users based on search criteria
    if (activateSpinner) {
      await this.withSpinner(() => this.model.loadUserList(criteria));
    } else {
      await this.model.loadUserList(criteria);
    }
  }

  async searchUsers(searchTerm: string, activateSpinner: boolean = true) {
    // Search for users by name
    await this.loadUsers({ query: "search", spec: searchTerm }, activateSpinner);
  }

  async loadUsersByType(listType: string, activateSpinner: boolean = true) {
    // Load users by type (e.g., "human", "all", "manual")
    await this.loadUsers({ query: listType, spec: "" }, activateSpinner);
  }

  // UI State Management Actions
  showSpinner() {
    // Show loading spinner
    this.model.spinners++;
    m.redraw();
  }

  hideSpinner() {
    // Hide loading spinner
    if (this.model.spinners > 0) {
      this.model.spinners--;
      m.redraw();
    }
  }

  async withSpinner<T>(asyncAction: () => Promise<T>): Promise<T> {
    // Execute an async action with spinner management
    this.showSpinner();
    try {
      const result = await asyncAction();
      return result;
    } finally {
      this.hideSpinner();
    }
  }

  // User Preference Management Actions
  async setUserPref(pref: Record<string, any>) {
    // Set a user preference on the server
    if (!this.model.state) return;
    try {
      await request(this.model.state, {
        method: "POST",
        url: "/setuserpref",
        body: pref
      }); // No result required or expected
      // Update the persisted settings in sessionStorage
      saveAuthSettings(this.model.state);
    } catch (e) {
      // A future TODO might be to signal an error in the UI
    }
  }

  async cancelFriendship(): Promise<boolean> {
    // Cancel the current user as a friend
    const user = this.model.user;
    const state = this.model.state;
    if (!user || !state) return false;

    try {
      const json: { ok: boolean; } = await request(state, {
        method: "POST",
        url: "/cancelplan",
        body: { }
      });
      if (json?.ok) {
        // Successfully cancelled: immediately update the friend and hasPaid state
        user.friend = false;
        state.hasPaid = false;
        state.plan = "";
        // Log a friendship cancellation event
        logEvent("cancel_plan", {
          userid: state.netskraflUserId,
          locale: state.locale,
          // Add plan identifiers here
          plan: "friend"
        });
        return true;
      }
    } catch(e) {
      // Handle error
    }
    return false;
  }

  attachListenerToRiddle(date: string, locale: string) {
    const { state } = this.model;
    const basePath = `gatadagsins/${date}/${locale}/`;

    // Listen to global best score
    attachFirebaseListener<BestScore>(basePath + "best",
      (json, firstAttach) => this.onRiddleGlobalScoreUpdate(json, firstAttach)
    );

    // Listen to group scores (if user has a group)
    if (state?.userGroupId) {
      attachFirebaseListener<BestScore>(basePath + `group/${state.userGroupId}/best`,
        (json, firstAttach) => this.onRiddleGroupScoreUpdate(json, firstAttach)
      );
    }

    // Listen to global leaderboard
    attachFirebaseListener(basePath + "leaders",
      (json, firstAttach) => this.onLeaderboardUpdate(json, firstAttach)
    );

    // Listen to user stats (if user is logged in)
    if (state?.netskraflUserId) {
      attachFirebaseListener(`gatadagsins/users/${locale}/${state.netskraflUserId}/stats`,
        (json, firstAttach) => this.onUserStatsUpdate(json, firstAttach)
      );
      // Listen to personal best move (entire achievement object)
      attachFirebaseListener(basePath + `achievements/${state.netskraflUserId}`,
        (json, firstAttach) => this.onPersonalBestScoreUpdate(json, firstAttach)
      );
    }
  }

  detachListenerFromRiddle(date: string, locale: string) {
    const { state } = this.model;
    const basePath = `gatadagsins/${date}/${locale}/`;
    detachFirebaseListener(basePath + "best");
    if (state?.userGroupId) {
      detachFirebaseListener(basePath + `group/${state.userGroupId}/best`);
    }
    detachFirebaseListener(basePath + "leaders");
    if (state?.netskraflUserId) {
      detachFirebaseListener(`gatadagsins/users/${locale}/${state.netskraflUserId}/stats`);
      detachFirebaseListener(basePath + `achievements/${state.netskraflUserId}`);
    }
  }

  onRiddleGlobalScoreUpdate(json: BestScore, firstAttach: boolean) {
    const { riddle } = this.model;
    const { score } = json;
    if (!riddle || !score || score <= 0) return;
    riddle.updateGlobalBestScore(json);
    m.redraw();
  }

  onRiddleGroupScoreUpdate(json: BestScore, firstAttach: boolean) {
    const { riddle } = this.model;
    const { score } = json;
    if (!riddle || !score || score <= 0) return;
    riddle.updateGroupBestScore(json);
    m.redraw();
  }

  onLeaderboardUpdate(json: any, firstAttach: boolean) {
    if (!json || typeof json !== 'object') {
      this.model.leaderboard = [];
    } else {
      // Convert dictionary to array and sort by score (desc), then timestamp (desc)
      const entries = Object.keys(json).map(userId => ({
        userId: json[userId].userId || userId,
        displayName: json[userId].displayName || '',
        score: json[userId].score || 0,
        timestamp: json[userId].timestamp || ''
      }));
      // Sort by score descending, then by timestamp ascending (earlier first)
      entries.sort((a, b) => {
        if (b.score !== a.score) {
          return b.score - a.score;
        }
        return a.timestamp.localeCompare(b.timestamp);
      });
      this.model.leaderboard = entries;
    }
    m.redraw();
  }

  onUserStatsUpdate(json: any, firstAttach: boolean) {
    if (!json) {
      this.model.userStats = null;
    } else {
      this.model.userStats = json;
    }
    m.redraw();
  }

  onPersonalBestScoreUpdate(json: any, firstAttach: boolean) {
    const { riddle } = this.model;
    if (!riddle || !json) return;

    // Extract the full move from Firebase
    const score: number = json.score || 0;
    const word: string = json.word || "";
    const coord: string = json.coord || "";
    const timestamp: string = json.timestamp || new Date().toISOString();

    // Only proceed if we didn't already have this score or better
    if (score <= riddle.personalBestScore || !word || !coord) return;

    riddle.personalBestScore = score;

    // Check if this move is already in playerMoves
    // (this is a safety precaution; normally the
    // move should not be there already)
    const moveExists = riddle.playerMoves.some(m =>
      m.word === word && m.coord === coord && m.score === score
    );

    // If not already present, add it to playerMoves
    // This enables clicking on it to see it on the board
    if (!moveExists) {
      riddle.playerMoves.push({
        word,
        score,
        coord,
        timestamp
      });
    }

    m.redraw();
  }

  async fetchRiddle(date: string, locale: string): Promise<void> {
    // Create the game via model
    if (!this.model) throw new Error("Model is not initialized");
    await this.model.initRiddle(date, locale);
    // Attach Firebase listeners
    this.attachListenerToRiddle(date, locale);
  }

  cleanupRiddle(date: string, locale: string): void {
    // Detach Firebase listeners
    this.detachListenerFromRiddle(date, locale);
  }

} // class Actions

export function createRouteResolver(actions: Actions, view: IView): m.RouteDefs {

  // Return a map of routes to onmatch and render functions

  let model = actions.model;
  // let state = model.state;

  return model.paths.reduce((acc, item) => {
    acc[item.route] = {

      // Navigating to a new route (passed in the second parameter)
      onmatch: (args: Params) => {
        // Automatically close all dialogs
        view.popAllDialogs();
        view.resetScale();
        actions.onNavigateTo(item.name, args, view);
      },

      // Render a view on a model
      render: () => { return view.appView(item.name); }

    };
    return acc;
  }, {} as m.RouteDefs);
}
